'use client';

import * as React from 'react';

import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/layout/dialog';

import { MobilePanel } from './MobilePanel';

type ListWithPreviewWorkspaceProps = {
  mobileListOpen: boolean;
  onMobileListOpenChange: (open: boolean) => void;
  mobileListDialogTitle: string;
  mobileListDialogDescription: string;
  mobileListPanelTitle: string;
  mobileListContent: React.ReactNode;
  mobileListBodyClassName?: string;
  mobileListContentContainerClassName?: string;
  mobileListUseFullWidthWrapper?: boolean;

  mobilePreviewOpen: boolean;
  onMobilePreviewOpenChange: (open: boolean) => void;
  mobilePreviewDialogTitle: string;
  mobilePreviewDialogDescription: string;
  mobilePreviewHeaderLabel: string;
  mobilePreviewContent: React.ReactNode;

  desktopListContent: React.ReactNode;
  desktopPreviewContent: React.ReactNode;
  desktopListWidthClassName?: string;
  desktopPreviewContainerClassName?: string;
  desktopPreviewContainerStyle?: React.CSSProperties;

  mobileEmptyState?: React.ReactNode;
  mobileBottomBar?: React.ReactNode;
};

export function ListWithPreviewWorkspace({
  mobileListOpen,
  onMobileListOpenChange,
  mobileListDialogTitle,
  mobileListDialogDescription,
  mobileListPanelTitle,
  mobileListContent,
  mobileListBodyClassName,
  mobileListContentContainerClassName,
  mobileListUseFullWidthWrapper = true,
  mobilePreviewOpen,
  onMobilePreviewOpenChange,
  mobilePreviewDialogTitle,
  mobilePreviewDialogDescription,
  mobilePreviewHeaderLabel,
  mobilePreviewContent,
  desktopListContent,
  desktopPreviewContent,
  desktopListWidthClassName,
  desktopPreviewContainerClassName,
  desktopPreviewContainerStyle,
  mobileEmptyState,
  mobileBottomBar,
}: ListWithPreviewWorkspaceProps) {
  return (
    <>
      <Dialog
        open={mobileListOpen}
        onOpenChange={onMobileListOpenChange}
      >
        <DialogContent className="left-0 top-0 flex h-dvh w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 p-0 min-[900px]:hidden [&>button]:right-3 [&>button]:top-3">
          <DialogTitle className="sr-only">{mobileListDialogTitle}</DialogTitle>
          <DialogDescription className="sr-only">
            {mobileListDialogDescription}
          </DialogDescription>
          <MobilePanel title={mobileListPanelTitle} bodyClassName={mobileListBodyClassName ?? 'bg-background p-0'}>
            <div className={cn(
              mobileListUseFullWidthWrapper && 'relative left-1/2 min-h-full w-screen max-w-none -translate-x-1/2',
              mobileListContentContainerClassName,
            )}>
              {mobileListContent}
            </div>
          </MobilePanel>
        </DialogContent>
      </Dialog>

      <Dialog
        open={mobilePreviewOpen}
        onOpenChange={onMobilePreviewOpenChange}
      >
        <DialogContent className="left-0 top-0 flex h-dvh w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 p-0 min-[900px]:hidden [&>button]:right-3 [&>button]:top-3">
          <DialogTitle className="sr-only">{mobilePreviewDialogTitle}</DialogTitle>
          <DialogDescription className="sr-only">
            {mobilePreviewDialogDescription}
          </DialogDescription>
          <div className="flex h-full min-h-0 flex-col bg-background">
            <div className="border-b border-border px-4 py-3 pr-14">
              <p className="text-overline text-foreground">{mobilePreviewHeaderLabel}</p>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden">
              {mobilePreviewContent}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex flex-1 min-h-0">
        <div className="hidden min-[900px]:flex w-full min-h-0 gap-3">
          <div className={cn('flex min-h-0 flex-col', desktopListWidthClassName ?? 'w-[320px]')}>
            {desktopListContent}
          </div>

          <div
            className={cn(
              'flex min-h-0 flex-1 border border-border bg-background overflow-hidden flex-col',
              desktopPreviewContainerClassName,
            )}
            style={desktopPreviewContainerStyle ?? { boxShadow: 'var(--shadow-warm-sm)' }}
          >
            {desktopPreviewContent}
          </div>
        </div>

        {!mobileListOpen && !mobilePreviewOpen && mobileEmptyState}
      </div>

      {mobileBottomBar}
    </>
  );
}
